import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { FeatureGate } from './components/ComingSoon';
import { RequireAuth, RequireRole } from './components/RequireAuth';
import { EmptyState } from './components/ui';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { FEATURES } from './lib/api';
import AiSettingsPage from './pages/AiSettings';
import AuditPage from './pages/Audit';
import ConnectionPage from './pages/Connection';
import Contact360Page from './pages/Contact360';
import ContactsPage from './pages/Contacts';
import ConversationPage from './pages/Conversation';
import FaqPage from './pages/Faq';
import InboxPage from './pages/Inbox';
import LibraryPage from './pages/Library';
import LoginPage from './pages/Login';
import SettingsPage from './pages/Settings';
import TestModePage from './pages/TestMode';
import { TicketDetailPage, TicketsPage } from './pages/Tickets';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5_000
    }
  }
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="/inbox" replace />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="inbox/:id" element={<InboxPage />} />
                <Route path="konusma/:id" element={<ConversationPage />} />
                <Route path="kisiler" element={<ContactsPage />} />
                <Route path="kisi/:id" element={<Contact360Page />} />

                <Route
                  path="tickets"
                  element={
                    <FeatureGate
                      available={FEATURES.tickets}
                      title="Ticket"
                      description="Ticket uçları (liste, detay, kanal seçerek yanıt, kapatma) API'de henüz yayınlanmadı. Gelen kutusu ve konuşma ekranları gerçek veriyle çalışıyor; ticket açma AI katmanıyla birlikte gelecek."
                      endpoints={['GET /api/tickets', 'GET /api/tickets/:id', 'POST /api/tickets/:id/reply', 'POST /api/tickets/:id/close']}
                    >
                      <TicketsPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="tickets/:id"
                  element={
                    <FeatureGate
                      available={FEATURES.tickets}
                      title="Ticket detayı"
                      description="Ticket thread'i (WhatsApp + e-posta) ve SLA sayacı, ticket uçları yayınlandığında bu ekranda görünecek."
                      endpoints={['GET /api/tickets/:id', 'POST /api/tickets/:id/reply']}
                    >
                      <TicketDetailPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="sss"
                  element={
                    <FeatureGate
                      available={FEATURES.faqs}
                      title="SSS"
                      description="Bilgi tabanı uçları (SSS CRUD + yeniden indeksleme) henüz yayınlanmadı. AI yanıtları bu tabloyu kullanacak."
                      endpoints={['GET /api/faqs', 'POST /api/faqs', 'PATCH /api/faqs/:id', 'POST /api/faqs/reindex']}
                    >
                      <FaqPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="kutuphane"
                  element={
                    <RequireRole>
                      <FeatureGate
                        available={FEATURES.library}
                        title="Kütüphane"
                        description="PDF yükleme, işleme kuyruğu ve SSS adayı onayı uçları henüz yok. Kütüphane hazır olduğunda AI, yüklenen belgelerden kaynak göstererek yanıt verecek."
                        endpoints={['POST /api/library/upload', 'GET /api/library', 'POST /api/library/:id/reindex', 'DELETE /api/library/:id']}
                      >
                        <LibraryPage />
                      </FeatureGate>
                    </RequireRole>
                  }
                />
                <Route
                  path="ai-ayarlari"
                  element={
                    <RequireRole>
                      <FeatureGate
                        available={FEATURES.aiSettings}
                        title="AI Ayarları"
                        description="AI ayarları uçları (model, effort, güven eşiği, bütçe) henüz yayınlanmadı. AI katmanı bağlandığında bu ekran gerçek değerleri gösterecek."
                        endpoints={['GET /api/ai/settings', 'PUT /api/ai/settings', 'POST /api/ai/test']}
                      >
                        <AiSettingsPage />
                      </FeatureGate>
                    </RequireRole>
                  }
                />
                <Route
                  path="test-modu"
                  element={
                    <RequireRole>
                      <TestModePage />
                    </RequireRole>
                  }
                />
                <Route
                  path="baglanti"
                  element={
                    <RequireRole>
                      <ConnectionPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="audit"
                  element={
                    <RequireRole>
                      <FeatureGate
                        available={FEATURES.audit}
                        title="Audit"
                        description="Audit ucu (filtreli kayıt listesi + CSV) henüz yayınlanmadı. Guard kararları şimdilik test modu ekranındaki son denemelerde görünüyor."
                        endpoints={['GET /api/admin/audit', 'GET /api/admin/policy-events']}
                      >
                        <AuditPage />
                      </FeatureGate>
                    </RequireRole>
                  }
                />
                <Route
                  path="ayarlar"
                  element={
                    <RequireRole>
                      <FeatureGate
                        available={FEATURES.settings}
                        title="Ayarlar"
                        description="Panel ayarları ucu henüz yok. WhatsApp bağlantısı için Bağlantı ekranı, limitler ve test modu için Test Modu ekranı kullanılabilir."
                        endpoints={['GET /api/settings', 'PUT /api/settings', 'GET/PUT /api/email/settings']}
                      >
                        <SettingsPage />
                      </FeatureGate>
                    </RequireRole>
                  }
                />
                <Route
                  path="*"
                  element={
                    <EmptyState
                      icon="search"
                      title="Sayfa bulunamadı"
                      hint="Adres yanlış olabilir. Gelen kutusuna dönerek devam edin."
                      action={
                        <a className="btn btn-sm" href="/inbox">
                          Gelen kutusu
                        </a>
                      }
                    />
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
